import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Calendar, CheckCircle, DownloadSimple, GoogleLogo, Plus, Spinner, Trash, Warning } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { api, ApiRequestError } from '@/lib/api'
import { addDaysToDateKey, createDefaultAvailabilityWindows, getDateKeyInTimeZone, minutesToTimeValue, TIME_ZONE_OPTIONS, timeValueToMinutes, type EditableAvailabilityWindow, WEEKDAY_LABELS } from '@/lib/availability'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'

type AvailabilityStepProps = {
  onNext: () => void
  onBack: () => void
  googleConnected: boolean
  availability: {
    configured: boolean
    timeZone: string | null
    activeWindowsCount: number
  } | null
  onRefresh: () => Promise<void>
  onGoToIntegrations: () => void
}

export function AvailabilityStep({
  onNext,
  onBack,
  googleConnected,
  availability,
  onRefresh,
  onGoToIntegrations,
}: AvailabilityStepProps) {
  const { t } = useTranslation('onboarding')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [timeZone, setTimeZone] = useState('America/Sao_Paulo')
  const [windows, setWindows] = useState<EditableAvailabilityWindow[]>([])
  const [importGoogle, setImportGoogle] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        setLoading(true)
        const [settings, windowsResponse] = await Promise.all([
          api.getTenantSettings(),
          api.getAvailabilityWindows(),
        ])

        if (cancelled) {
          return
        }

        setTimeZone(settings.timeZone || 'America/Sao_Paulo')
        setWindows(
          windowsResponse.length > 0
            ? windowsResponse.map((window) => ({
                id: window.id,
                weekday: window.weekday,
                startMinutes: window.startMinutes,
                endMinutes: window.endMinutes,
                label: window.label ?? '',
                active: window.active ?? true,
              }))
            : createDefaultAvailabilityWindows(),
        )
      } catch (error) {
        const message = error instanceof Error ? error.message : t('availability.loadError')
        toast.error(message)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [t])

  const activeWindowCount = useMemo(
    () => windows.filter((window) => (window.active ?? true) && window.endMinutes > window.startMinutes).length,
    [windows],
  )

  const importRange = useMemo(() => {
    const fromDate = getDateKeyInTimeZone(new Date(), timeZone || 'America/Sao_Paulo')
    return {
      fromDate,
      toDate: addDaysToDateKey(fromDate, 89),
    }
  }, [timeZone])

  const canContinue = Boolean(timeZone) && activeWindowCount > 0 && !saving && !loading

  const updateWindow = (index: number, patch: Partial<EditableAvailabilityWindow>) => {
    setWindows((previous) => previous.map((window, currentIndex) => (
      currentIndex === index
        ? { ...window, ...patch }
        : window
    )))
  }

  const addWindow = (weekday: number) => {
    setWindows((previous) => [
      ...previous,
      {
        weekday,
        startMinutes: 9 * 60,
        endMinutes: 18 * 60,
        label: '',
        active: true,
      },
    ])
  }

  const removeWindow = (index: number) => {
    setWindows((previous) => previous.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleSave = async () => {
    if (!timeZone) {
      toast.error(t('availability.validation.timeZoneRequired'))
      return
    }

    if (activeWindowCount === 0) {
      toast.error(t('availability.validation.windowRequired'))
      return
    }

    try {
      setSaving(true)
      const response = await api.saveOnboardingAvailability({
        timeZone,
        windows: windows.map((window) => ({
          weekday: window.weekday,
          startMinutes: window.startMinutes,
          endMinutes: window.endMinutes,
          label: window.label?.trim() || null,
          active: window.active ?? true,
        })),
        importGoogle: googleConnected && importGoogle ? {
          enabled: true,
          fromDate: importRange.fromDate,
          toDate: importRange.toDate,
          mergeMode: 'append_only',
        } : undefined,
      })

      await onRefresh()

      if (response.googleImport.requested) {
        toast.success(t('availability.savedWithImport', {
          count: response.googleImport.importedExceptionsCount,
        }))
      } else {
        toast.success(t('availability.saved'))
      }

      onNext()
    } catch (error) {
      if (error instanceof ApiRequestError) {
        toast.error(error.message)
      } else {
        toast.error(t('availability.saveError'))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0B2A4A] to-[#F08A24] mb-4">
          <Calendar weight="bold" className="w-7 h-7 text-white" />
        </div>
        <div className="flex items-center justify-center gap-2 mb-2">
          <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white">
            {t('availability.title')}
          </h2>
          {availability?.configured ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              {t('availability.configured')}
            </Badge>
          ) : null}
        </div>
        <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          {t('availability.subtitle')}
        </p>
      </div>

      <div className="max-w-4xl mx-auto w-full space-y-6">
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="space-y-2">
              <Label>{t('availability.timeZone')}</Label>
              <Select value={timeZone} onValueChange={setTimeZone}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_ZONE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('availability.timeZoneHint')}
              </p>
            </div>

            <div className="rounded-xl border border-dashed border-[var(--pd-border)] bg-[var(--pd-muted)]/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('availability.summaryTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('availability.summaryHint', { count: activeWindowCount })}
                  </p>
                </div>
                <Badge variant={activeWindowCount > 0 ? 'default' : 'secondary'}>
                  {activeWindowCount > 0 ? t('availability.ready') : t('availability.incomplete')}
                </Badge>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="w-4 h-4 animate-spin" />
                {t('availability.loading')}
              </div>
            ) : (
              <div className="space-y-4">
                {WEEKDAY_LABELS.map((label, weekday) => {
                  const weekdayWindows = windows
                    .map((window, index) => ({ window, index }))
                    .filter(({ window }) => window.weekday === weekday)

                  return (
                    <div key={label} className="rounded-xl border border-[var(--pd-border)] p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            {t(`availability.weekdays.${label.toLowerCase()}`)}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {weekdayWindows.length > 0
                              ? t('availability.dayConfigured', { count: weekdayWindows.length })
                              : t('availability.dayEmpty')}
                          </p>
                        </div>

                        <Button type="button" variant="outline" size="sm" onClick={() => addWindow(weekday)}>
                          <Plus className="w-4 h-4 mr-2" />
                          {t('availability.addWindow')}
                        </Button>
                      </div>

                      {weekdayWindows.length === 0 ? (
                        <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                          {t('availability.noWindows')}
                        </div>
                      ) : null}

                      <div className="space-y-3">
                        {weekdayWindows.map(({ window, index }) => (
                          <div key={`${window.id ?? 'new'}-${index}`} className="grid gap-3 rounded-lg border border-[var(--pd-border)] p-3 md:grid-cols-[120px_120px_1fr_auto_auto]">
                            <div className="space-y-1.5">
                              <Label>{t('availability.start')}</Label>
                              <Input
                                type="time"
                                value={minutesToTimeValue(window.startMinutes)}
                                onChange={(event) => updateWindow(index, { startMinutes: timeValueToMinutes(event.target.value) })}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label>{t('availability.end')}</Label>
                              <Input
                                type="time"
                                value={minutesToTimeValue(window.endMinutes)}
                                onChange={(event) => updateWindow(index, { endMinutes: timeValueToMinutes(event.target.value) })}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label>{t('availability.label')}</Label>
                              <Input
                                value={window.label ?? ''}
                                onChange={(event) => updateWindow(index, { label: event.target.value })}
                                placeholder={t('availability.labelPlaceholder')}
                              />
                            </div>

                            <div className="flex items-end gap-2">
                              <Checkbox
                                checked={window.active ?? true}
                                onCheckedChange={(checked) => updateWindow(index, { active: checked === true })}
                                id={`active-window-${weekday}-${index}`}
                              />
                              <Label htmlFor={`active-window-${weekday}-${index}`}>{t('availability.active')}</Label>
                            </div>

                            <div className="flex items-end">
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeWindow(index)}>
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={cn(
          googleConnected
            ? 'border-blue-200 bg-blue-50/40 dark:bg-blue-950/20'
            : 'border-amber-200 bg-amber-50/40 dark:bg-amber-950/20',
        )}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                googleConnected ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-amber-100 dark:bg-amber-900/40',
              )}>
                {googleConnected ? (
                  <GoogleLogo className="w-6 h-6 text-blue-600" />
                ) : (
                  <Warning className="w-6 h-6 text-amber-600" />
                )}
              </div>

              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {googleConnected ? t('availability.googleImport.title') : t('availability.googleImport.disconnectedTitle')}
                  </h3>
                  {googleConnected ? (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                      {t('availability.googleImport.optional')}
                    </Badge>
                  ) : null}
                </div>

                <p className="text-sm text-muted-foreground">
                  {googleConnected ? t('availability.googleImport.description') : t('availability.googleImport.disconnectedDescription')}
                </p>

                {googleConnected ? (
                  <>
                    <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-white/70 px-3 py-2 dark:bg-blue-950/10">
                      <Checkbox
                        checked={importGoogle}
                        onCheckedChange={(checked) => setImportGoogle(checked === true)}
                        id="import-google-calendar"
                      />
                      <Label htmlFor="import-google-calendar" className="text-sm font-medium">
                        {t('availability.googleImport.checkbox')}
                      </Label>
                    </div>

                    <div className="rounded-lg bg-white/70 px-3 py-2 text-xs text-muted-foreground dark:bg-blue-950/10">
                      <div className="flex items-center gap-2 mb-1">
                        <DownloadSimple className="w-4 h-4" />
                        <span>{t('availability.googleImport.snapshotHint')}</span>
                      </div>
                      <p>
                        {t('availability.googleImport.rangeHint', {
                          from: importRange.fromDate,
                          to: importRange.toDate,
                        })}
                      </p>
                    </div>
                  </>
                ) : (
                  <Button type="button" variant="outline" onClick={onGoToIntegrations}>
                    {t('availability.googleImport.goToIntegrations')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between mt-10 max-w-4xl mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <Button
          onClick={handleSave}
          disabled={!canContinue}
          className="bg-[#F08A24] hover:bg-[#e07a14] text-white"
        >
          {saving ? <Spinner className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          {saving ? t('availability.saving') : t('availability.saveAndContinue')}
          <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
